/**
 * React hooks for Hospital API operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hospitalsApi, Hospital, HospitalDetail, HospitalCreateData, HospitalUpdateData } from '../api/hospitals';
import { toast } from 'sonner';

/**
 * Hook to fetch all hospitals
 */
export function useHospitals(params?: { is_active?: boolean; search?: string }) {
  return useQuery({
    queryKey: ['hospitals', params],
    queryFn: () => hospitalsApi.getAll(params),
    select: (data) => data.hospitals,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a single hospital by ID
 */
export function useHospital(hospitalId: number | null) {
  return useQuery({
    queryKey: ['hospitals', hospitalId],
    queryFn: () => hospitalsApi.getById(hospitalId!),
    enabled: !!hospitalId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create a new hospital
 */
export function useCreateHospital() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: HospitalCreateData) => hospitalsApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['hospitals'] });
      toast.success('Hospital Created', {
        description: response.message || 'Hospital created successfully',
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to create hospital';
      toast.error('Creation Failed', {
        description: errorMessage,
      });
    },
  });
}

/**
 * Hook to update a hospital
 */
export function useUpdateHospital() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hospitalId, data }: { hospitalId: number; data: HospitalUpdateData }) =>
      hospitalsApi.update(hospitalId, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hospitals'] });
      queryClient.invalidateQueries({ queryKey: ['hospitals', variables.hospitalId] });
      toast.success('Hospital Updated', {
        description: response.message || 'Hospital updated successfully',
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to update hospital';
      toast.error('Update Failed', {
        description: errorMessage,
      });
    },
  });
}

/**
 * Hook to delete a hospital
 */
export function useDeleteHospital() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hospitalId: number) => hospitalsApi.delete(hospitalId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['hospitals'] });
      toast.success('Hospital Deleted', {
        description: response.message || 'Hospital deleted successfully',
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || 'Failed to delete hospital';
      toast.error('Deletion Failed', {
        description: errorMessage,
      });
    },
  });
}